import React, { useState } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  Input,
  FormGroup,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  TextField,
  FormHelperText
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import styles from "../../SiteManagement/styles"; //TODO EXPORT THESE HIGHER!
import {
  SiteDTO,
  CustomerDTO,
  LTIPlatformDTO,
} from "@beable/roster-backend-client-v1";
import { SUBPAGES } from "../constants";
import { ModalCustomerSelector } from '../../Common/CustomerSelector';
import { ModalSiteSelector } from '../../Common/SiteSelector';
import EnvironmentSelector from "../../Common/Selectors/EnvironmentSelector";
import { InputLengthHelperText } from "../../Common/FormModal";

const getStyles = styles.addModalStyles;
interface LTIPlatformFormProps {
  onSubmit: (ltiPlatform: LTIPlatformDTO, allEnvs: boolean) => void;
  handleClose: () => void;
  selectedItem?: LTIPlatformDTO;
  site?: SiteDTO | null;
}

export default function LTIPlatformForm(props: LTIPlatformFormProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [allEnvs, setEnableForAllEnvs] = useState(false);
  const [LTIPlatform, setLTIPlatform] = useState<LTIPlatformDTO>(
    props.selectedItem ?? SUBPAGES.LTIProviders.defaultState
  );
  const [selectedCustomer, setSelectedCustomer] = useState<
    CustomerDTO | undefined
  >(undefined);

  const checkLTIPlatformFieldsValues = () => {
    return !(
      LTIPlatform.name.trim().length &&
      LTIPlatform.siteId &&
      LTIPlatform.issuer.trim().length &&
      LTIPlatform.clientId.trim().length &&
      LTIPlatform.deploymentId.trim().length &&
      (LTIPlatform.keyset?.trim().length ||
        LTIPlatform.keysetUrl?.trim().length) &&
      LTIPlatform.authRedirectUrl.trim().length &&
      LTIPlatform.userLookupField.trim().length &&
      LTIPlatform.logoutRedirectUrl.trim().length
    );
  };

  const LTIPlatformDataChangeHandler = (e: any) => {
    const {
      target: { name, value },
    } = e;
    setLTIPlatform((ltiPlatform: LTIPlatformDTO) => {
      return { ...ltiPlatform, [name]: value };
    });
  };

  const handleCustomerChange = (customer: CustomerDTO) => {
    setSelectedCustomer(customer);
    setLTIPlatform((ltiPlatform: LTIPlatformDTO) => {
      return { ...ltiPlatform, customer: customer?.id };
    });
  };

  const handleSiteChange = (site: SiteDTO) => {
    setLTIPlatform((ltiPlatform: LTIPlatformDTO) => {
      return { ...ltiPlatform, siteId: site?.id, siteName: site?.subdomain };
    });
  };

  const handleSetActive = (isActive: boolean) =>
    setLTIPlatform((ltiPlatform: any) => {
      return { ...ltiPlatform, active: isActive };
    });

  const handleSetUseStaticLaunch = (useStaticLaunch: boolean) =>
    setLTIPlatform((ltiPlatform: any) => {
      return { ...ltiPlatform, useStaticLaunch: useStaticLaunch };
    });
    
  const handleSetAllEnvs = (e: any) => setEnableForAllEnvs(e.target.checked);

  const resetState = () => {
    setEnableForAllEnvs(false);
    setSelectedCustomer(undefined);
    setLTIPlatform(SUBPAGES.LTIProviders.defaultState);
  };

  const submitForm = async () => {
    props.onSubmit(LTIPlatform, allEnvs);
  };

  const getCustomerId = () => {
    if (props.site) {
      return props.site.customerId;
    }

    return selectedCustomer?.id ?? "";
  };

  return <>
    <FormGroup>
      <FormControl variant="standard" margin="dense">
        <InputLabel htmlFor="name">Name*</InputLabel>
        <Input
          onChange={LTIPlatformDataChangeHandler}
          value={LTIPlatform.name}
          name="name"
          id="name"
          aria-describedby="provider name"
        />
        <InputLengthHelperText str={LTIPlatform.name} />
      </FormControl>
      <FormControl
        variant="standard"
        margin="dense"
        sx={allEnvs ? styles.dnone : styles.dflex}>
        <EnvironmentSelector
          env={LTIPlatform.environment}
          onChange={LTIPlatformDataChangeHandler}
          disabled={!!props.selectedItem}
          labelText={"Environment*"}
        />
      </FormControl>
      <FormControl variant="standard" margin="dense">
        <ModalCustomerSelector
          onSelect={handleCustomerChange}
          required
          customerId={getCustomerId()}
          disabled={!!props.selectedItem}
        />
      </FormControl>
      <FormControl variant="standard" margin="dense">
        <ModalSiteSelector
          customerId={getCustomerId()}
          siteId={props.selectedItem?.siteId ?? undefined}
          disabled={!selectedCustomer || !!props.selectedItem}
          onSelect={handleSiteChange}
          required
        />
      </FormControl>
      {!props.selectedItem && (
        <FormControlLabel
          control={
            <Checkbox
              name="allEnvs"
              sx={styles.cbs}
              checked={allEnvs}
              onChange={handleSetAllEnvs}
            />
          }
          label="Add for all environments"
        />
      )}
      <FormControl variant="standard" margin="dense">
        <InputLabel htmlFor="issuer">Issuer*</InputLabel>
        <Input
          onChange={LTIPlatformDataChangeHandler}
          value={LTIPlatform.issuer}
          name="issuer"
          id="issuer"
          aria-describedby="issuer"
        />
        <FormHelperText sx={styles.dflex}>
          LTI Platform (ie Canvas) Issuer value
        </FormHelperText>
      </FormControl>
      <FormControl variant="standard" margin="dense">
        <InputLabel htmlFor="authRedirectUrl">Auth Redirect URL*</InputLabel>
        <Input
          onChange={LTIPlatformDataChangeHandler}
          value={LTIPlatform.authRedirectUrl}
          name="authRedirectUrl"
          id="authRedirectUrl"
          aria-describedby="LTI platform auth redirect URL"
        />
        <InputLengthHelperText str={LTIPlatform.authRedirectUrl} />
        <FormHelperText sx={styles.dflex}>
          LTI Platform (ie Canvas) auth redirect URL
        </FormHelperText>
      </FormControl>
      <FormControl variant="standard" margin="dense">
        <InputLabel htmlFor="clientId">Client ID*</InputLabel>
        <Input
          onChange={LTIPlatformDataChangeHandler}
          value={LTIPlatform.clientId}
          name="clientId"
          id="clientId"
          aria-describedby="clientId"
        />
        <FormHelperText sx={styles.dflex}>
          ClientID value registered on the remote LTI Platform
        </FormHelperText>
      </FormControl>
      <FormControl variant="standard" margin="dense">
        <InputLabel htmlFor="deploymentId">Deployment ID*</InputLabel>
        <Input
          onChange={LTIPlatformDataChangeHandler}
          name="deploymentId"
          value={LTIPlatform.deploymentId}
          id="deploymentId"
          aria-describedby="deploymentId"
        />
        <InputLengthHelperText str={LTIPlatform.deploymentId} />
        <FormHelperText sx={styles.dflex}>
          DeploymentId value registered on the remote LTI Platform
        </FormHelperText>
      </FormControl>
      <FormControl variant="standard" margin="dense">
        <InputLabel id="userIdentifierPickerLabel" htmlFor="userIdentifierPicker">
          User Identifier Type*
        </InputLabel>
        <Select
          variant="standard"
          labelId="userIdentifierPickerLabel"
          id="userIdentifierPicker"
          name="userLookupField"
          value={LTIPlatform.userLookupField}
          onChange={LTIPlatformDataChangeHandler}>
          <MenuItem value="externalSourceId" key="externalSourceId">
            externalSourceId
          </MenuItem>
          <MenuItem value="username" key="username">
            username
          </MenuItem>
          <MenuItem value="email" key="email">
            email
          </MenuItem>
        </Select>
        <FormHelperText sx={styles.dflex}>
          User identifier to take from UserAccount and LoginTicket
        </FormHelperText>
      </FormControl>
      <FormControl variant="standard" margin="dense">
        <TextField
          variant="standard"
          label="Keyset"
          multiline
          rows={3}
          onChange={LTIPlatformDataChangeHandler}
          value={LTIPlatform.keyset}
          name="keyset"
          id="keyset"
          aria-describedby="keyset"
          disabled />
      </FormControl>
      <FormControl variant="standard" margin="dense">
        <InputLabel htmlFor="keysetUrl">Keyset URL</InputLabel>
        <Input
          onChange={LTIPlatformDataChangeHandler}
          name="keysetUrl"
          value={LTIPlatform.keysetUrl ?? ''}
          id="keysetUrl"
          aria-describedby="keysetUrl"
        />
        <InputLengthHelperText str={LTIPlatform.keysetUrl} />
      </FormControl>
      <FormControl variant="standard" margin="dense">
        <InputLabel htmlFor="logoutRedirectUrl">
          Logout Redirect URL*
        </InputLabel>
        <Input
          onChange={LTIPlatformDataChangeHandler}
          value={LTIPlatform.logoutRedirectUrl}
          name="logoutRedirectUrl"
          id="logoutRedirectUrl"
        />
        <InputLengthHelperText str={LTIPlatform.logoutRedirectUrl} />
        <FormHelperText sx={styles.dflex}>
          URL to launch on user LOGOUT
        </FormHelperText>
      </FormControl>
      <FormControl variant="standard" margin="dense">
        <InputLabel htmlFor="identifierTransforms">
          Identifier transforms
        </InputLabel>
        <Input
          onChange={LTIPlatformDataChangeHandler}
          value={LTIPlatform.identifierTransforms ?? ''}
          name="identifierTransforms"
          id="identifierTransforms"
          placeholder="trim,upper,lower"
        />
        <FormHelperText sx={styles.dflex}>
          Transforms to apply to the LTI Request user identifier
        </FormHelperText>
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox
            name="useStaticLaunch"
            sx={styles.cbs}
            checked={!!LTIPlatform.useStaticLaunch}
            onChange={() => handleSetUseStaticLaunch(!LTIPlatform.useStaticLaunch)}
          />
        }
        label="Use Static Launch (iFrame)"
      />
      <FormControlLabel
        control={
          <Checkbox
            name="active"
            sx={styles.cbs}
            checked={!!LTIPlatform.active}
            onChange={() => handleSetActive(!LTIPlatform.active)}
          />
        }
        label="Active"
      />
    </FormGroup>
    {/* BUTTONS */}
    <FormGroup sx={[styles.mt2, styles.dfr]}>
      <Button
        variant="contained"
        color="primary"
        disabled={checkLTIPlatformFieldsValues()}
        onClick={submitForm}
      >
        {props.selectedItem ? "Edit" : "Add LTI platform"}
      </Button>
      <Button
        variant="contained"
        onClick={() => {
          resetState();
          props.handleClose();
        }}
        sx={styles.ml2}>
        Close
      </Button>
    </FormGroup>
  </>;
}